/** @jsx jsx */

import React, {
  Fragment as _,
  createContext,
  useLayoutEffect,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { css, jsx } from '@emotion/core';
import getBlockById from 'services/apiHelpers/getBlockById';
import { useDispatch } from 'react-redux';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';
import { questionXofXDefaultText } from 'components/Editor/EditAssessment/EditAssessment';

const Background = ({
  background,
  backgroundImageOpacity,
  backgroundBlur = 0,
}) => {
  let cachedBackgroundImage = useCachedResource(CHAMELEON_CACHE_v1, background);

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        ...(background && {
          background: `url('${cachedBackgroundImage ||
            background}') center / cover no-repeat`,
        }),

        opacity: backgroundImageOpacity / 100,
        filter: `blur(${backgroundBlur}px)`,
        // scale the thing up to not have blurry edges
        ...(backgroundBlur > 0 && {
          transformOrigin: 'center',
          transform: 'scale(1.1)',
        }),
        pointerEvents: 'none',
      }}
    />
  );
};

const BackgroundWithParallax = props => {
  const {
    state,
    interactions,
    background,
    backgroundImageOpacity,
    backgroundBlur = 0,
    parallaxValue = 10,
    width,
    height,
  } = props;

  const cachedBackgroundImage = useCachedResource(
    CHAMELEON_CACHE_v1,
    background
  );

  const [
    refreshParallaxScrollOnTopicChange,
    setRefreshParallaxScrollOnTopicChange,
  ] = useState<boolean>(false);

  const img = useRef<HTMLImageElement | null>(null);
  const setImageRef = useCallback(node => {
    img.current = node;
  }, []);

  const minHeight = `${100 + parallaxValue}vh`;

  useEffect(() => {
    if (!img.current) {
      return;
    }

    const parallaxThrottler = () => {
      // we can implement whatever more specific throttling approach...
      requestAnimationFrame(parallax);
    };

    const parallax = () => {
      if (!img.current) {
        return;
      }

      const { innerHeight, scrollY } = window;
      const imgTravel = img.current.offsetHeight - innerHeight;
      const scrollTravel = innerHeight * (interactions.length - 1);
      const scrollUnit = imgTravel / scrollTravel;
      const scrollValue = scrollY * scrollUnit;

      if (scrollValue > imgTravel) {
        return;
      }

      //
      img.current.style.transform = `translateY(-${scrollValue}px)`;
    };
    //

    window.addEventListener('scroll', parallaxThrottler);
    window.addEventListener('resize', parallaxThrottler);
    parallax();

    return () => {
      window.removeEventListener('scroll', parallaxThrottler);
      window.removeEventListener('resize', parallaxThrottler);
    };
  }, [setImageRef, refreshParallaxScrollOnTopicChange]);

  useEffect(() => {
    if (!img.current) {
      return;
    }

    let tick;

    if (document.body.scrollHeight > window.innerHeight) {
      const imgHeightOnLoad = img.current.offsetHeight;

      let checkLimit = 0;

      tick = setInterval(() => {
        if (!img.current) {
          return;
        }

        if (img.current.offsetHeight !== imgHeightOnLoad) {
          clearInterval(tick);
          setRefreshParallaxScrollOnTopicChange(true);
        }

        //
        if (checkLimit >= 300) {
          clearInterval(tick);
        }
        checkLimit += 1;
        //
      }, 1);
    }

    return () => {
      clearInterval(tick);
    };
  }, [setImageRef, setRefreshParallaxScrollOnTopicChange]);

  return (
    <_>
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <img
          ref={setImageRef}
          src={cachedBackgroundImage || background}
          style={{
            all: 'initial',
            position: 'absolute',
            objectFit: 'cover',
            minWidth: '100vw',
            minHeight,
            opacity: backgroundImageOpacity / 100,
            filter: `blur(${backgroundBlur}px)`,
          }}
        />
      </div>
    </_>
  );
};

// could / would / should put this in the store / redux
interface IAssessmentContext {
  assessmentId: string;
  submitted: {
    [id: string]: {
      answer: string[];
      correctAnswer: string[];
      isCorrect: boolean | undefined;
      attemptCount: number;
    };
  };
  resetQuestion: (id: string) => void;
  setSubmitted: (
    id: string,
    answer: string[],
    correctAnswer: string[],
    isCorrect: boolean
  ) => void;
  assessmentQuestionSubmitLabel?: string | undefined;
  assessmentQuestionContinueLabel?: string | undefined;
  maxAttempts: undefined | number;
  questionXofXtext: string;
  answersWrappingAlignment?: string;
  saveData?: boolean;
  learnMoreResetsAssessment?: boolean;
  name: string;
  scoring: boolean | undefined;
}

const GradientBackground = ({ backgroundGradientData }) => {
  const {
    backgroundPrimaryColor,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
  } = backgroundGradientData || {};

  return (
    <div
      css={css({
        position: 'fixed',
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${backgroundPrimaryColor})`,
      })}
    />
  );
};

const AssessmentContext = createContext<IAssessmentContext>({
  assessmentId: '',
  submitted: {},
  resetQuestion: () => {},
  setSubmitted: () => {},
  assessmentQuestionSubmitLabel: undefined,
  assessmentQuestionContinueLabel: undefined,
  maxAttempts: undefined,
  questionXofXtext: questionXofXDefaultText,
  answersWrappingAlignment: 'center',
  saveData: false,
  learnMoreResetsAssessment: false,
  name: '',
  scoring: undefined,
});

const AssessmentContainer = props => {
  const { theme, topic: assessment, children } = props as any;

  //
  const {
    assessmentQuestionSubmitLabel,
    assessmentQuestionContinueLabel,
    maxAttempts,
    questionXofXtext = questionXofXDefaultText,
    answersWrappingAlignment,
    saveData = false,
    learnMoreResetsAssessment = false,
  } = assessment?.props;

  const dispatch = useDispatch();

  const [submitted, setSubmitted] = useState<{
    [id: string]: {
      answer: string[];
      correctAnswer: string[];
      isCorrect: boolean;
      attemptCount: number;
    };
  }>({});

  const block: any = getBlockById(assessment.id);

  const { name, scoring } = assessment.props;

  let {
    sId,
    src,
    backgroundImage,
    backgroundImageOpacity = 100,
    width,
    height,
    backgroundBlur = 0,
    parallax = false,
    parallaxValue = 10,
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    backgroundGradientFullHeight = false,
  }: any = getBlockById(assessment.id).props;

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    backgroundImage = src;
  }

  let backgroundColor =
    block.props.backgroundColor || theme.backgroundColor || '';

  if (backgroundGradient && backgroundGradientFullHeight) {
    backgroundColor = `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${block.props.backgroundColor})`;
  }

  const textColor = block.props.textColor || theme.textColor || '';

  const opacity = backgroundImageOpacity / 100;

  return (
    <AssessmentContext.Provider
      value={
        {
          assessmentId: assessment.id,
          name: assessment.props.name,
          scoring: assessment.props.scoring,
          submitted,
          resetQuestion(id: string) {
            const updatedSubmissions = JSON.parse(JSON.stringify(submitted));
            delete updatedSubmissions[id];

            setSubmitted(updatedSubmissions);
            //
          },
          setSubmitted(
            id: string,
            answer: string[],
            correctAnswer: string[],
            isCorrect: boolean
          ) {},
          assessmentQuestionSubmitLabel,
          assessmentQuestionContinueLabel,
          maxAttempts,
          questionXofXtext,
          answersWrappingAlignment,
          saveData,
          learnMoreResetsAssessment,
        } as IAssessmentContext
      }
    >
      <div
        className="fader_-5s"
        id="main"
        tabIndex={-1}
        css={css`
          position: relative;
          background: ${backgroundColor};
        `}
      >
        {backgroundGradient && !backgroundGradientFullHeight && (
          <GradientBackground
            backgroundGradientData={{
              backgroundGradientAngle,
              backgroundPrimaryColor: backgroundColor,
              backgroundSecondaryColor,
            }}
          />
        )}

        {window.dev() || !parallax ? (
          <Background
            background={backgroundImage}
            backgroundImageOpacity={backgroundImageOpacity}
            backgroundBlur={backgroundBlur}
          />
        ) : (
          <BackgroundWithParallax
            interactions={children.props.interactions}
            background={backgroundImage}
            backgroundImageOpacity={backgroundImageOpacity}
            backgroundBlur={backgroundBlur}
            parallaxValue={parallaxValue}
            width={width}
            height={height}
          />
        )}

        {children}
      </div>
    </AssessmentContext.Provider>
  );
};

export default AssessmentContainer;
export { AssessmentContext };
